import type { CustomerGroup } from '../services/customerGroupsApi';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MoreHorizontal, Search, Trash2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useDeleteCustomerGroup } from '../hooks/useCustomerGroups';
import { EditGroupDialog } from './EditGroupDialog';
import { GroupDiscountsDialog } from './GroupDiscountsDialog';
import { CustomerGroupCriteriaDialog } from './CustomerGroupCriteriaDialog';
import { useConfirm, withConfirmSuccess } from '@/shared/contexts/confirm-context';
// import { GroupDiscountDialog } from './GroupDiscountsDialog';

interface CustomerGroupsTableProps {
  groups: CustomerGroup[];
  searchValue: string;
  onSearchChange: (value: string) => void;
}

export const CustomerGroupsTable = ({ groups, searchValue, onSearchChange }: CustomerGroupsTableProps) => {
  const deleteMutation = useDeleteCustomerGroup();
  const confirm = useConfirm();

  const handleDelete = (id: string, name: string) => {
    confirm({
      title: 'Delete customer group?',
      description: `The group “${name}” will be permanently removed. This can't be undone.`,
      variant: 'destructive',
      confirmLabel: 'Delete',
      onConfirm: () =>
        withConfirmSuccess(deleteMutation.mutateAsync(id), `${name} deleted`),
    });
  };

  return (
    <div className="space-y-4">
      <div className="relative w-64 ml-auto">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <Input
          placeholder="Search groups..."
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-10"
        />
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Group Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Default Terms</TableHead>
              <TableHead>Credit Limit</TableHead>
              <TableHead>Discount %</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {groups.map((group) => (
              <TableRow key={group.id}>
                <TableCell className="font-medium">
                  {group.name}
                  {group.isDefault && (
                    <Badge variant="secondary" className="ml-2 text-xs">
                      Default
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="break-words max-w-xs">{group.description}</TableCell>
                <TableCell>{group.defaultPaymentTerms}</TableCell>
                <TableCell>
                  {group.defaultCreditLimit.toLocaleString()}
                </TableCell>
                <TableCell>{group.defaultDiscountPercentage}%</TableCell>
                <TableCell>
                  <Badge
                    variant={group.isActive ? 'default' : 'destructive'}
                  >
                    {group.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuItem asChild>
                        <EditGroupDialog group={group} />
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <GroupDiscountsDialog group={group} />
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <CustomerGroupCriteriaDialog
                          groupId={group.id}
                          groupName={group.name}
                        />
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={() => handleDelete(group.id, group.name)}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {groups.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No customer groups found
        </div>
      )}
    </div>
  );
};